import type { JSX } from 'react';
import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

// Sidebar / Chat
const MODAL_Z_INDEX = 3000;

interface ModalPortalProps {
    children: React.ReactNode;
    isOpen: boolean;
}

/**
 * Portal component for rendering modals at the document body level
 * Inputs: children (React nodes), isOpen (boolean flag)
 * Output: renders children in a portal when isOpen is true; no return value
 * Side effects: creates/destroys DOM element and appends/removes from document body
 */
export function ModalPortal({ children, isOpen }: ModalPortalProps): JSX.Element | null {
    const portalRoot = document.body;
    const container = useMemo(() => document.createElement('div'), []);

    useEffect(() => {
        if (!isOpen) return undefined;
        portalRoot.appendChild(container);
        return () => {
            if (portalRoot.contains(container)) {
                portalRoot.removeChild(container);
            }
        };
    }, [container, portalRoot, isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: MODAL_Z_INDEX,
            }}
        >
            {children}
        </div>,
        container,
    );
}